import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { AccountingCallbackPayload } from '@/core/models/trial-balance.models';
import { errorMessage } from '@/core/notify/notify';
import { trialBalanceService } from '@/core/services/trialBalance';
import { Button } from '@/kit/Button';
import { announceOAuthResult } from './accountingOAuth';
import './AccountingOAuthCallback.scss';

type Outcome =
  | { status: 'working' }
  | { status: 'done'; providerName: string; connectedTo: string | null }
  | { status: 'failed'; message: string };

/** What the provider's redirect carried: `code` and `state`, or `error` (e.g. access_denied) and `state`. */
export function readCallbackParams(search: string): AccountingCallbackPayload {
  const params = new URLSearchParams(search);
  return {
    state: params.get('state') ?? '',
    code: params.get('code'),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
    realmId: params.get('realmId'),
  };
}

/**
 * BUG-038: where Xero, QuickBooks and FreeAgent send the user back after the sign-in popup (the redirect URI registered
 * with the provider is this page). It hands the code and state to the API as the signed-in user - the API validates the
 * state, exchanges the code and stores the tokens - and tells the window that opened the popup, then closes. The tokens
 * never reach the browser.
 */
export function AccountingOAuthCallback() {
  const [payload] = useState(() => readCallbackParams(window.location.search));
  const [outcome, setOutcome] = useState<Outcome>(() =>
    payload.state
      ? { status: 'working' }
      : {
          status: 'failed',
          message:
            'This page is where the sign-in returns to. Start from Tasks > Trial Balance > Add Trial Balance and select Connect.',
        },
  );
  // The state is single-use: the guard keeps a second run of the effect (development double-mount) from spending it twice.
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !payload.state) {
      return;
    }
    started.current = true;

    trialBalanceService.completeIntegrationCallback(payload).then(
      (result) => {
        setOutcome({ status: 'done', providerName: result.providerName, connectedTo: result.connectedTo });
        announceOAuthResult({ ok: true, provider: result.provider, message: result.connectedTo });
        // Closing is only for a popup; a page opened any other way stays to show the result.
        if (window.opener) {
          window.setTimeout(() => window.close(), 400);
        }
      },
      (error) => {
        const message = errorMessage(error);
        setOutcome({ status: 'failed', message });
        announceOAuthResult({ ok: false, provider: '', message });
      },
    );
  }, [payload]);

  return (
    <div className="oauth-callback">
      <div className="oauth-callback-card">
        {outcome.status === 'working' && (
          <>
            <h2>Connecting</h2>
            <p>Finishing the sign-in, one moment.</p>
          </>
        )}
        {outcome.status === 'done' && (
          <>
            <h2>Connected to {outcome.providerName}</h2>
            <p>
              {outcome.connectedTo ? `${outcome.connectedTo} is connected. ` : ''}
              You can close this window; the trial balance import continues in the application.
            </p>
            <Button className="action-button primary-button" type="button" onClick={() => window.close()}>
              Close this window
            </Button>
          </>
        )}
        {outcome.status === 'failed' && (
          <>
            <h2>Not connected</h2>
            <p className="oauth-callback-error" role="alert">
              {outcome.message}
            </p>
            <Button className="action-button secondary-button" type="button" onClick={() => window.close()}>
              Close this window
            </Button>
            <p className="mt-1">
              <Link to="/home">Back to the application</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
